import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import ReportsListItem from '../../../ui/listItems/ReportsListItem';
import {
  chatSquareSVG,
  trash2SVG,
  editSVG,
  tableSVG,
  chartSVG,
} from '../../../lib/allCustomSVGs';
import get_custom_prompts_api_call, {
  delete_custom_prompt_api_call,
} from '../../../lib/api/custom-prompts/custom-prompts-api-call';
import { CustomPrompt } from '../../../interfaces/CustomPrompt';
import { RootState } from '../../../store/store';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../../ui/noData/NoData';

type Props = {
  refreshTrigger?: number;
  onEditCustomPrompt?: (customPrompt: CustomPrompt) => void;
};

const ReportsCustomPrompts = ({
  refreshTrigger,
  onEditCustomPrompt,
}: Props) => {
  const { t } = useTranslation();
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const isAgencyUser = userProfile?.role === 'agency';
  const [customPrompts, setCustomPrompts] = useState<CustomPrompt[]>([]);
  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');
  const [deletingCustomPromptId, setDeletingCustomPromptId] = useState<
    string | null
  >(null);

  const fetchCustomPrompts = async () => {
    setLoading('pending');
    try {
      const customPromptsData = await get_custom_prompts_api_call();

      if (customPromptsData && customPromptsData.length > 0) {
        setCustomPrompts(customPromptsData);
        setLoading('success');
      } else {
        setLoading('null');
      }
    } catch (error) {
      console.error('Error fetching custom prompts:', error);
      setLoading('error');
    }
  };

  useEffect(() => {
    fetchCustomPrompts();
  }, [refreshTrigger]);

  const handleEditCustomPrompt = (customPrompt: CustomPrompt) => {
    if (onEditCustomPrompt) {
      onEditCustomPrompt(customPrompt);
    }
  };

  const handleDeleteCustomPrompt = async (customPromptId: string) => {
    setDeletingCustomPromptId(customPromptId);
    try {
      const success = await delete_custom_prompt_api_call(customPromptId);
      if (success) {
        setCustomPrompts((prev) => prev.filter((p) => p.id !== customPromptId));
      }
    } catch (error) {
      console.error('Error deleting custom prompt:', error);
    } finally {
      setDeletingCustomPromptId(null);
    }
  };

  if (loading === 'pending') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <LoadingSpinner />
      </div>
    );
  }

  if (loading === 'error') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <NoData
          title={t('ErrorFetchingData')}
          subTitle={t('SomethingWentWrong-msg')}
        />
      </div>
    );
  }

  if (loading === 'null') {
    return (
      <div className='containerWithPaddingTopOnMobile'>
        <SettingsCommonCard
          mainTitle={t('YourCustomPrompts')}
          subTitle={t('SavedAIPromptsForAnalysis')}
          titleFontSize='2rem'
          titleMarginBottom='0.5rem'
          childrenStyles={{ marginTop: '2rem' }}
        >
          <NoData
            title={t('NoCustomPromptsFound')}
            subTitle={t('AddYourFirstCustomPrompt')}
          />
        </SettingsCommonCard>
      </div>
    );
  }

  const getIconAndColorByOutputType = (outputType: string) => {
    if (outputType === 'graph') {
      return { icon: chartSVG, color: '#069C47' };
    } else if (outputType === 'table') {
      return { icon: tableSVG, color: '#1462CF' };
    } else {
      return { icon: chatSquareSVG, color: '#a855f7' };
    }
  };

  return (
    <div className='containerWithPaddingTopOnMobile'>
      <SettingsCommonCard
        mainTitle={t('YourCustomPrompts')}
        subTitle={t('SavedAIPromptsForAnalysis')}
        titleFontSize='2rem'
        titleMarginBottom='0.5rem'
        childrenStyles={{
          marginTop: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {customPrompts.map((customPrompt) => {
          const isDeleting = deletingCustomPromptId === customPrompt.id;
          return (
            <ReportsListItem
              key={customPrompt.id}
              id={customPrompt.id}
              icon={
                getIconAndColorByOutputType(
                  customPrompt?.output_type?.presentation
                ).icon
              }
              iconColor={
                getIconAndColorByOutputType(
                  customPrompt?.output_type?.presentation
                ).color
              }
              title={customPrompt.name}
              subTitle={customPrompt.prompt}
              shortCode={customPrompt.short_code}
              syncStatusLabel={
                isAgencyUser
                  ? customPrompt.is_in_sync_group
                    ? t('Synced')
                    : t('NotSynced')
                  : undefined
              }
              syncStatusIsSynced={
                isAgencyUser
                  ? customPrompt.is_in_sync_group ?? false
                  : undefined
              }
              onConfigClick={() => {
                if (!isDeleting) {
                  handleDeleteCustomPrompt(customPrompt.id);
                }
              }}
              configIcon={isDeleting ? null : trash2SVG}
              configIconColor='#FF4B4B'
              {...(isDeleting && { configLoading: true })}
              onEditClick={() => {
                if (!isDeleting) {
                  handleEditCustomPrompt(customPrompt);
                }
              }}
              editIcon={editSVG}
              editIconColor='#6B7280'
              editIconSize='1.4rem'
            />
          );
        })}
      </SettingsCommonCard>
    </div>
  );
};

export default ReportsCustomPrompts;
